import React, { useEffect, useRef, useState } from "react";
import { __ } from "services/Translator";
import Slider from "react-slick";
import api from "services/api";
import { filterByCountry } from "utils/registrationStates";
import {
  getRequestCountry,
  resolveGeoCountry,
  COUNTRY_CHANGED_EVENT,
} from "services/requestCountry";
import DOMPurify from "dompurify";

// Slim, CMS-managed promo strip shown above the game categories on the games
// page. Modeled on HomeFullBanner's fetch/country/CTA plumbing, but deliberately
// renders NOTHING when the content team has not created any record (no hardcoded
// default) so the games page stays clean until a banner is curated.
export default function GamesPageBanner(props) {
  const [banners, setBanners] = useState([]);
  // Out-of-order guard: a country switch can fire a second fetch while the first
  // is still in flight, so only the latest response is allowed to set state.
  const requestSeqRef = useRef(0);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    fade: true,
    cssEase: "linear",
    arrows: true,
  };

  const getBanners = async () => {
    const seq = ++requestSeqRef.current;
    try {
      // Resolve the effective country (manual -> account -> geo) BEFORE the
      // request so the X-Request-Country header and the client-side filter use
      // the same country on a cold first load.
      await resolveGeoCountry(api);
      const visitorCountry = getRequestCountry();
      const response = await api.get("/cms/games-page-banner/true", {
        sort: "sortOrder,ASC",
      });
      if (seq !== requestSeqRef.current) return; // superseded by a newer switch
      if (response.status === 200) {
        const data = response?.data?.data ?? [];
        setBanners(
          filterByCountry(Array.isArray(data) ? data : [], visitorCountry)
        );
      } else {
        setBanners([]);
      }
    } catch (error) {
      console.error("Failed to fetch games-page banners:", error);
      if (seq === requestSeqRef.current) setBanners([]);
    }
  };

  useEffect(() => {
    getBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch in place when the visitor's country changes (manual pick or geo
  // resolving), keeping the current strip visible until the new one arrives.
  useEffect(() => {
    const onChange = () => getBanners();
    window.addEventListener(COUNTRY_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(COUNTRY_CHANGED_EVENT, onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignupClick = () => {
    const setter = props.setModalSignUpPropsData;
    if (typeof setter === "function") {
      setter(true);
      setTimeout(() => setter(false), 500);
    } else if (process.env.NODE_ENV !== "production") {
      console.warn("[GamesPageBanner] Missing handler for setModalSignUpPropsData");
    }
  };

  const handleDepositClick = () => {
    if (typeof props.setOpenWalletModalTab === "function") {
      props.setOpenWalletModalTab("deposit");
    } else if (process.env.NODE_ENV !== "production") {
      console.warn("[GamesPageBanner] Missing handler for setOpenWalletModalTab");
    }
  };

  const renderBanner = (item, index) => {
    const safeTitle = item?.title || "";
    const safeDescription = item?.description || "";
    const ctaForGuest = item?.buttonTextNonLoggedIn || __("Get Lucky Now!");
    const ctaForUser = item?.buttonTextLoggedIn || __("Deposit Now!");

    return (
      <div className="banner-container" key={item?.id ?? index}>
        <picture className="banner-image-container">
          <source
            srcSet={item?.imageMobile || item?.imageMediumDesktop}
            media="(max-width: 600px)"
            type="image/webp"
          />
          <source
            srcSet={item?.imageTablet || item?.imageMediumDesktop}
            media="(max-width: 900px)"
            type="image/webp"
          />
          <source
            srcSet={item?.imageMediumDesktop || item?.imageLargeDesktop}
            media="(max-width: 1200px)"
            type="image/webp"
          />
          <source
            srcSet={item?.imageLargeDesktop || item?.imageMediumDesktop}
            media="(min-width: 1201px)"
            type="image/webp"
          />
          <img
            src={
              item?.imageMediumDesktop ||
              item?.imageLargeDesktop ||
              item?.imageTablet ||
              item?.imageMobile
            }
            alt="Games Promo Banner"
            className="banner-img"
          />
        </picture>

        <div className="bannerContent">
          {safeTitle ? (
            <h1
              className="white"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(safeTitle.replace(/\n/g, "<br />")),
              }}
            />
          ) : null}
          {item?.subHeading ? <h1 className="orange">{item.subHeading}</h1> : null}
          {safeDescription ? <p>{safeDescription}</p> : null}

          {!props.isAuthenticated && (
            <button type="button" onClick={handleSignupClick} className="commonBtn pOneBtn">
              {ctaForGuest}
            </button>
          )}

          {props.isAuthenticated && (
            <button type="button" onClick={handleDepositClick} className="commonBtn pOneBtn">
              {ctaForUser}
            </button>
          )}
        </div>
      </div>
    );
  };

  // No content = no strip. Never fall back to a hardcoded default.
  if (banners.length === 0) return null;

  return (
    <div className="gamesPageBanner">
      {banners.length > 1 ? (
        <Slider
          key={banners
            .map((b) => b.id ?? b.imageLargeDesktop ?? b.title)
            .join("|")}
          {...settings}
        >
          {banners.map(renderBanner)}
        </Slider>
      ) : (
        banners.slice(0, 1).map(renderBanner)
      )}
    </div>
  );
}
